import './qbooksAccountLineItemsSection.scss';

import { TransparentButton } from '@approvalmax/ui';
import { intl } from '@approvalmax/utils';
import { useSetAtom } from 'jotai';
import { selectors } from 'modules/common';
import { domain } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import { AddMatchingIcon, DoneIcon } from 'modules/sprites';
import { LineItemsTable } from 'pages/requestList/components/lineItemsSection/LineItemsTable';
import SectionContainer from 'pages/requestList/components/SectionContainer';
import { checkAccessOnlyAsWatcher } from 'pages/requestList/selectors/requestSelectors';
import { ColumnDefinition } from 'pages/requestList/selectors/types/ColumnDefinition';
import { FC, memo, useMemo } from 'react';
import bemFactory from 'react-bem-factory';
import { QBooksMatchingInfo, qBooksMatchingPopupAccountLineItemState } from 'shared/components/(matchings)';

import LineItemsSection from '../../card/LineItemsSection';
import { DescriptionCell } from '../QBooksLineItemsSection/QBooksLineItemsSection.styles';
import { messages } from './QBooksAccountLineItemsSection.messages';
import { QBooksAccountLineItemsSectionProps } from './QBooksAccountLineItemsSection.types';

const bem = bemFactory.block('reql-qbooks-account-line-items-section');
const qa = bemFactory.qa('reql-qbooks-account-line-items-section');

const QBooksAccountLineItemsSection: FC<QBooksAccountLineItemsSectionProps> = memo((props) => {
    const { className, request, readonly, onRemoveMatching, isRemoveMatchingLoading } = props;

    const details = request.details;

    const setAccountLineItem = useSetAtom(qBooksMatchingPopupAccountLineItemState);

    const hasTaxes = details.lineAmountType !== domain.LineAmountType.NoTax;
    const isBill = request.integrationCode === domain.IntegrationCode.QBooksBill;
    const isExpense = request.integrationCode === domain.IntegrationCode.QBooksExpense;
    const isPo = request.integrationCode === domain.IntegrationCode.QBooksPo;
    const isApproved = request.statusV2 === domain.RequestStatusV2.Approved;

    const manualMatchingEnabled = request.company.billMatchingSettings?.manualMatchingEnabled ?? false;
    const showManualMatching =
        manualMatchingEnabled &&
        selectors.company.getQBOMatchingIsAvailable(request.company) &&
        (isBill || (isPo && isApproved));
    const isQBOMatchingOnEditingBetaSupported = request.company.betaFeatures.includes(
        domain.CompanyBetaFeature.QBOMatchingOnEditing
    );

    const { id: profileId } = useSelector(selectors.profile.getProfileUser);
    const me = useMemo(
        () => request.company.allMembers.find((user) => user.id === profileId),
        [profileId, request.company.allMembers]
    );
    const hasAccessOnlyAsWatcher = useMemo(
        () => !!me && checkAccessOnlyAsWatcher(me.databaseId || '', me.userEmail, request.company, request),
        [me, request]
    );

    const columnDefinitions = useMemo(() => {
        const columnDefs: Array<ColumnDefinition<domain.QBooksAccountLineItem>> = [
            {
                id: 'account',
                name: messages.accountColumnName,
                value: (li) => (li.account ? li.account.text : null),
            },
            {
                id: 'description',
                name: messages.descriptionColumnName,
                value: (li) => li.description,
                cell: (value) => <DescriptionCell>{value}</DescriptionCell>,
            },
        ];

        if (hasTaxes) {
            columnDefs.push({
                id: 'taxCodeName',
                name: messages.taxColumnName,
                value: (li) => (li.taxCode ? li.taxCode.text : null),
            });
        }

        if (isBill || isExpense) {
            columnDefs.push({
                id: 'billable',
                name: messages.billableColumnName,
                value: (li) => {
                    return li.isBillable ? messages.billableColumnTooltip : null;
                },
                alignRight: false,
                width: 50,
                cell: (val) => val && <DoneIcon width={13} height={10} className={bem('done-icon')} />,
            });
            columnDefs.push({
                id: 'taxable',
                name: messages.taxableColumnName,
                value: (li) => {
                    return li.isTaxable ? messages.taxableColumnTooltip : null;
                },
                alignRight: false,
                width: 30,
                cell: (val) => val && <DoneIcon width={13} height={10} className={bem('done-icon')} />,
            });
        }

        columnDefs.push(
            {
                id: 'customerName',
                name: messages.customerColumnName,
                value: (li) => (li.customer ? li.customer.text : null),
            },
            {
                id: 'className',
                name: messages.classColumnName,
                value: (li) => (li.class ? li.class.text : null),
            },
            {
                id: 'amount',
                name: messages.amountColumnName({
                    currency: request.currency,
                }),
                value: (li) => intl.formatNumber(li.amount || 0),
                ...(!showManualMatching && {
                    width: 90,
                    alignRight: true,
                    alwaysVisible: true,
                }),
            }
        );

        if (showManualMatching) {
            const matchingCommands = selectors.request.getQBooksMatchingCommands(
                request,
                profileId,
                hasAccessOnlyAsWatcher
            );

            columnDefs.push({
                id: 'linked',
                name: messages.linkedColumnName,
                value: () => messages.matching,
                cell: (_val, li) => {
                    if (readonly) {
                        return null;
                    }

                    const isMatched = isQBOMatchingOnEditingBetaSupported
                        ? Boolean(li.matchedLineItemIds?.length || li.matchedLineItemId)
                        : li.isMatched;

                    if (isMatched) {
                        return (
                            <QBooksMatchingInfo
                                isAccountLineItem
                                lineItemId={li.id}
                                profileId={profileId}
                                request={request}
                                onRemoveMatching={onRemoveMatching}
                                isRemoveMatchingLoading={isRemoveMatchingLoading}
                            />
                        );
                    }

                    if (matchingCommands.addMatching.disabled) {
                        return '—';
                    }

                    return (
                        <TransparentButton
                            execute={() => setAccountLineItem(li)}
                            command={matchingCommands.addMatching}
                        >
                            <AddMatchingIcon />
                        </TransparentButton>
                    );
                },
                width: 40,
                alignRight: true,
                alwaysVisible: true,
            });
        }

        return columnDefs;
    }, [
        hasAccessOnlyAsWatcher,
        hasTaxes,
        isBill,
        isExpense,
        isQBOMatchingOnEditingBetaSupported,
        isRemoveMatchingLoading,
        onRemoveMatching,
        profileId,
        readonly,
        request,
        setAccountLineItem,
        showManualMatching,
    ]);

    if (details.accountLineItems.length === 0) {
        return null;
    }

    return (
        <SectionContainer className={className} text={messages.sectionHeaderText}>
            <LineItemsSection
                qa={qa()}
                renderTable={() => (
                    <LineItemsTable columnDefinitions={columnDefinitions} lineItems={details.accountLineItems} />
                )}
                renderFooter={() => null}
            />
        </SectionContainer>
    );
});

QBooksAccountLineItemsSection.displayName = 'QBooksAccountLineItemsSection';

export default QBooksAccountLineItemsSection;
